import { HttpClient } from '@angular/common/http';
import { Injectable, Injector, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

const AUDIO_DISABLED_STORAGE_ITEM = 'audio_disabled';
const AUDIO_VOLUME_STORAGE_ITEM = 'audio_volume';
const DEFAULT_AUDIO_VOLUME = 5;

@Injectable({
  providedIn: 'root',
})
export class AudioService {

  public currentVolumeSubject: BehaviorSubject<number>;
  public audioEnabledSubject: BehaviorSubject<boolean>;

  private currentAudioVolume: number;
  private isAudioEnabled: boolean;

  /**
   * Constructor
   * @param {HttpClient} http
   * @param {NgZone} ngZone
   * @param {Router} router
   * @param {Injector} injector
   */
  public constructor(
    private http: HttpClient,
    private ngZone: NgZone,
    private router: Router,
    private injector: Injector,
  ) {
    // Determine whether audio should be turned on or off by initially checking the cookie
    this.isAudioEnabled = !this.audioDisabledByUser();

    // The volume is zero is the audio is not enabled. Else we check the local storage for the desired volume.
    this.currentAudioVolume = this.isAudioEnabled ? (this.volumeSetByUser() || DEFAULT_AUDIO_VOLUME) : 0;

    this.currentVolumeSubject = new BehaviorSubject<number>(this.currentAudioVolume );
    this.audioEnabledSubject = new BehaviorSubject<boolean>(this.isAudioEnabled);
  }

  /**
   * Checks if audio was disabled by the user
   *
   * @return {boolean}
   */
  public audioDisabledByUser(): boolean {
    return Boolean(JSON.parse(localStorage.getItem(AUDIO_DISABLED_STORAGE_ITEM)));
  }

  /**
   * Checks if audio was disabled by the user
   *
   * @return {boolean}
   */
  public saveUpdatedVolume(): void {
    localStorage.setItem(AUDIO_DISABLED_STORAGE_ITEM, JSON.stringify(this.currentAudioVolume === 0));
    localStorage.setItem(AUDIO_VOLUME_STORAGE_ITEM, JSON.stringify(this.currentAudioVolume));
  }

  /**
   * Checks the volume stored in the storage
   *
   * @return {number}
   */
  public volumeSetByUser(): number {
    return JSON.parse(localStorage.getItem(AUDIO_VOLUME_STORAGE_ITEM)) || undefined;
  }

  /**
   * Getter for the current volume
   *
   * @return number
   */
  public get currentVolume(): number {
    return this.currentAudioVolume;
  }

  /**
   * Getter for audio being enabled
   *
   * @return boolean
   */
  public get audioEnabled(): boolean {
    return this.isAudioEnabled;
  }

  /**
   * Toggles the audio for the entire application. If the status is not set, we toggle isAudioEnabled directly.
   *
   * @return void
   */
  public toggleAudio(): void {
    const audioEnabledAfterAction: boolean = !this.isAudioEnabled;

    // Determine the volume to be set
    const newVolume: number = this.determineAudioVolume(audioEnabledAfterAction);
    this.updateCurrentVolume(newVolume);
  }

  /**
   * Make sure that, based on the volume, the audio is marked as enabled or disabled.
   * Propagates the current volume to all subscribers
   *
   * @return void
   */
  public updateCurrentVolume(volume: number): void {

    const updatedAudioEnabled: boolean = (volume > 0);
    if (updatedAudioEnabled !== this.audioEnabled) {
      this.isAudioEnabled = !this.isAudioEnabled;
      this.audioEnabledSubject.next(
        this.isAudioEnabled,
      );
    }

    this.currentAudioVolume = volume;
    this.currentVolumeSubject.next(
      this.currentAudioVolume,
    );

    this.saveUpdatedVolume();
  }

  /**
   * Returns whether the audio is enabled.
   *
   * @return Observable
   */
  public getAudioEnabled(): Observable<boolean> {
    return this.audioEnabledSubject.asObservable();
  }

  /**
   * Returns whether the audio is enabled.
   *
   * @return Observable
   */
  public getCurrentVolume(): Observable<number> {
    return this.currentVolumeSubject.asObservable();
  }

  /**
   * Look at storage to see if volume was changed by the user
   * This depends on whether the audio is enabled or not
   *
   * @returns {number}
   */
  private determineAudioVolume(audioEnabled: boolean): number {
    return audioEnabled ? (this.volumeSetByUser() || DEFAULT_AUDIO_VOLUME) : 0;
  }

}
